import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Building2, Ticket, Clock, CheckCircle, XCircle,
  MapPin, TrendingUp, AlertCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CityAdminPanel Component
 * Dashboard for city administrators to manage their specific tenant/city
 */
export const CityAdminPanel = ({ adminId, tenantId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingBusinesses, setPendingBusinesses] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch city dashboard
        const dashResponse = await axios.get(`${API}/stats/admin/city/${tenantId}`, {
          headers: { 'X-User-Id': adminId }
        });
        setData(dashResponse.data);
        
        // Fetch pending businesses for this tenant
        const bizResponse = await axios.get(`${API}/admin/pending-businesses`, {
          headers: { 'X-User-Id': adminId },
          params: { tenant_id: tenantId }
        });
        setPendingBusinesses(bizResponse.data || []);
      } catch (err) {
        console.error('Error fetching city admin data:', err);
        setError(err.response?.data?.detail || 'Error loading city data');
      } finally {
        setLoading(false);
      }
    };

    if (adminId && tenantId) {
      fetchData();
    }
  }, [adminId, tenantId]);

  const handleApprove = async (businessId) => {
    try {
      await axios.post(`${API}/admin/approve-business/${businessId}`, {}, {
        headers: { 'X-User-Id': adminId }
      });
      setPendingBusinesses(prev => prev.filter(b => b.id !== businessId));
    } catch (err) {
      console.error('Error approving business:', err);
    }
  };

  const handleReject = async (businessId) => {
    if (!window.confirm(t('city_admin.reject') + '?')) return;
    
    try {
      await axios.post(`${API}/admin/reject-business/${businessId}`, {}, {
        headers: { 'X-User-Id': adminId }
      });
      setPendingBusinesses(prev => prev.filter(b => b.id !== businessId));
    } catch (err) {
      console.error('Error rejecting business:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl">
        {error}
      </div>
    );
  }

  if (!data) return null;

  const { tenant, stats, pending_approvals, recent_reservations_24h, top_businesses } = data;

  return (
    <div className="space-y-6" data-testid="city-admin-panel">
      {/* City Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white"
      >
        <div className="flex items-center gap-3 mb-2">
          <MapPin className="w-6 h-6" />
          <h2 className="text-2xl font-bold">{tenant?.name}</h2>
          <span className={`px-3 py-1 rounded-full text-xs ${
            tenant?.is_active ? 'bg-green-400/20' : 'bg-red-400/20'
          }`}>
            {tenant?.is_active ? t('super_admin.active') : t('super_admin.inactive')}
          </span>
        </div>
        <p className="text-purple-100">{t('city_admin.title')}</p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-gray-500">{t('super_admin.total_users')}</span>
          </div>
          <p className="text-2xl font-bold">{stats?.total_users?.toLocaleString() || 0}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-gray-500">{t('super_admin.total_businesses')}</span>
          </div>
          <p className="text-2xl font-bold">{stats?.total_businesses?.toLocaleString() || 0}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center gap-2 mb-2">
            <Ticket className="w-5 h-5 text-emerald-500" />
            <span className="text-sm text-gray-500">{t('super_admin.active_kupons')}</span>
          </div>
          <p className="text-2xl font-bold">{stats?.active_kupons?.toLocaleString() || 0}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-amber-500" />
            <span className="text-sm text-gray-500">{t('city_admin.recent_activity')}</span>
          </div>
          <p className="text-2xl font-bold">{recent_reservations_24h || 0}</p>
        </motion.div>
      </div>

      {/* Pending Approvals & Top Businesses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Business Approvals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            {t('city_admin.pending_approvals')}
            {pending_approvals > 0 && (
              <span className="bg-amber-100 text-amber-700 text-xs px-2 py-1 rounded-full">
                {pending_approvals}
              </span>
            )}
          </h3>
          
          {pendingBusinesses.length > 0 ? (
            <div className="space-y-3">
              {pendingBusinesses.map((business) => (
                <div 
                  key={business.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl"
                >
                  <div>
                    <p className="font-medium">{business.businessName || business.name}</p>
                    <p className="text-sm text-gray-500">{business.email}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(business.id)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(business.id)}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('city_admin.pending_approvals')}: 0</p>
            </div>
          )}
        </motion.div>

        {/* Top Businesses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            {t('city_admin.top_businesses')}
          </h3>
          
          {top_businesses && top_businesses.length > 0 ? (
            <div className="space-y-3">
              {top_businesses.map((business, index) => (
                <div 
                  key={business._id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 ? 'bg-amber-100 text-amber-700' :
                      index === 1 ? 'bg-gray-200 text-gray-700' :
                      index === 2 ? 'bg-amber-700/20 text-amber-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {index + 1}
                    </span>
                    <p className="font-medium">{business.name}</p>
                  </div>
                  <span className="text-emerald-600 font-semibold">
                    {business.count} {t('super_admin.completed')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('city_admin.top_businesses')}</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CityAdminPanel;
